import {
    type CSSProperties,
    type ReactNode,
    useState
} from "react";
import {
    View
} from "react-native";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitLocalize,
    type DirectionType,
    DirectionProvider,
    ToggleGroup,
    Button,
    Badge,
    Alert,
    Card,
    Icon,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    ArrowRight
} from "lucide-react-native";
import {
    BulletList,
    InlineCode,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section,
    Cards
} from "../../components/content";
import {
    PlusIcon
} from "../../anatomy/icons";

const NAME = "DirectionProvider";

const USAGE_CODE = `<DirectionProvider
    direction="rtl"
    lang="ar"
>
    <Alert
        title="لديك رسالة جديدة"
        type="info"
    />
</DirectionProvider>

const {
    direction
} = NCoreUIKitLocalize.useContext();`;

const ISLAND_CODE = `<DirectionProvider
    direction="rtl"
    lang="ar"
>
    <Text>
        رقم الهاتف:
    </Text>
    <DirectionProvider
        direction="ltr"
    >
        <Text>
            +90 555 123 45 67
        </Text>
    </DirectionProvider>
</DirectionProvider>`;

const PHONE = "+90 555 123 45 67";

const DIRECTIONS: Array<DirectionType> = [
    "ltr",
    "rtl"
];

const PROVIDER_STYLE = {
    gap: 16
};

const ROW_STYLE = {
    flexDirection: "row",
    gap: 8
} as const;

const SAMPLE_STYLE = {
    flexDirection: "row",
    alignItems: "center",
    gap: 8
} as const;

const ITEM_ROW_STYLE = {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8
} as const;

const MAX_ITEMS = 8;

const CELL_STYLE: CSSProperties = {
    flexDirection: "column",
    display: "flex",
    gap: 8
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("directionProviderPage.resolutionTitle"),
        id: "resolution"
    },
    {
        title: t("directionProviderPage.islandTitle"),
        id: "island"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const DirectionBadge = () => {
    const {
        direction
    } = NCoreUIKitLocalize.useContext();

    return <View
        style={SAMPLE_STYLE}
    >
        <Icon
            icon={ArrowRight}
            size="small"
            color="mid"
        />
        <Text
            variant="labelMediumSize"
            color="mid"
        >
            {`direction → ${direction}`}
        </Text>
    </View>;
};

const DirectionProviderPage = () => {
    const {
        language,
        t
    } = useDocsI18n();

    const [
        direction,
        setDirection
    ] = useState<DirectionType>("ltr");

    const [
        itemCount,
        setItemCount
    ] = useState(0);

    const isRtl = direction === "rtl";

    const handleAddItem = () => {
        setItemCount((current) => Math.min(current + 1, MAX_ITEMS));
    };

    const handleDirectionChange = (value: string | Array<string>) => {
        const nextDirection = DIRECTIONS.find((item) => item === value);

        if(nextDirection) {
            setDirection(nextDirection);
        }
    };

    const renderCell = (label: string, node: ReactNode) => <div
        style={CELL_STYLE}
        key={label}
    >
        <Text
            variant="labelSmallSize"
            color="mid"
        >
            {label}
        </Text>
        <Card>
            <Card.Content>
                {node}
            </Card.Content>
        </Card>
    </div>;

    return <Article>
        <ComponentHeader
            lead={t("directionProviderPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("directionProviderPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("directionProviderPage.overviewCaption")}
                isStacked={true}
            >
                <ToggleGroup
                    title={t("directionProviderPage.directionLabel")}
                    onValueChange={handleDirectionChange}
                    attachBehaviour="attached"
                    selectBehaviour="single"
                    isClearEnabled={false}
                    value={direction}
                    variant="outline"
                    size="small"
                >
                    {
                        DIRECTIONS.map((item) => <ToggleGroup.Item
                            title={item}
                            value={item}
                            key={item}
                        />)
                    }
                </ToggleGroup>
                <DirectionProvider
                    lang={isRtl ? "ar" : language}
                    style={PROVIDER_STYLE}
                    direction={direction}
                >
                    <Alert
                        actions={[
                            {
                                title: isRtl ? t("directionProviderPage.arView") : t("directionProviderPage.view"),
                                onPress: () => undefined
                            }
                        ]}
                        title={isRtl ? t("directionProviderPage.arTitle") : t("sample.notification")}
                        description={isRtl ? t("directionProviderPage.arBody") : t("sample.body")}
                        type="info"
                    />
                    <Button
                        title={isRtl ? t("directionProviderPage.arAddItem") : t("directionProviderPage.addItem")}
                        isDisabled={itemCount >= MAX_ITEMS}
                        onPress={handleAddItem}
                        variant="outline"
                        icon={PlusIcon}
                        type="neutral"
                    />
                    {
                        itemCount ?
                            <View
                                style={ITEM_ROW_STYLE}
                            >
                                {
                                    Array.from({
                                        length: itemCount
                                    }, (_, index) => <Badge
                                        title={t(isRtl ? "directionProviderPage.arItem" : "directionProviderPage.item", [
                                            index + 1
                                        ])}
                                        variant="tonal"
                                        type="neutral"
                                        size="small"
                                        key={index}
                                    />)
                                }
                            </View>
                        :
                            null
                    }
                    <DirectionBadge/>
                </DirectionProvider>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
            <Cards
                items={[
                    {
                        text: t("directionProviderPage.guideText"),
                        title: t("page.direction"),
                        href: "/direction"
                    }
                ]}
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("directionProviderPage.resolutionTitle")}
            id="resolution"
        >
            <Paragraph>
                {t("directionProviderPage.resolutionText")}
            </Paragraph>
            <Preview
                caption={t("directionProviderPage.resolutionCaption")}
                isStacked={true}
            >
                {renderCell(t("directionProviderPage.rowNone"), <DirectionBadge/>)}
                {
                    renderCell("direction=\"rtl\"", <DirectionProvider
                        direction="rtl"
                    >
                        <DirectionBadge/>
                    </DirectionProvider>)
                }
                {
                    renderCell(t("directionProviderPage.rowNested"), <DirectionProvider
                        direction="rtl"
                    >
                        <DirectionProvider
                            direction="ltr"
                        >
                            <DirectionBadge/>
                        </DirectionProvider>
                    </DirectionProvider>)
                }
                {
                    renderCell(t("directionProviderPage.rowInherit"), <DirectionProvider
                        direction="rtl"
                    >
                        <DirectionProvider>
                            <DirectionBadge/>
                        </DirectionProvider>
                    </DirectionProvider>)
                }
            </Preview>
        </Section>
        <Section
            title={t("directionProviderPage.islandTitle")}
            id="island"
        >
            <Paragraph>
                {t("directionProviderPage.islandText")}
            </Paragraph>
            <Preview
                caption={t("directionProviderPage.islandCaption")}
                isStacked={true}
            >
                {
                    renderCell(t("directionProviderPage.islandMixed"), <DirectionProvider
                        direction="rtl"
                        lang="ar"
                    >
                        <Text
                            variant="bodyMediumSize"
                            color="high"
                        >
                            {`${t("directionProviderPage.arPhone")} ${PHONE}`}
                        </Text>
                    </DirectionProvider>)
                }
                {
                    renderCell(t("directionProviderPage.islandIsolated"), <DirectionProvider
                        style={ROW_STYLE}
                        direction="rtl"
                        lang="ar"
                    >
                        <Text
                            variant="bodyMediumSize"
                            color="high"
                        >
                            {t("directionProviderPage.arPhone")}
                        </Text>
                        <DirectionProvider
                            direction="ltr"
                        >
                            <Text
                                variant="bodyMediumSize"
                                color="high"
                            >
                                {PHONE}
                            </Text>
                        </DirectionProvider>
                    </DirectionProvider>)
                }
            </Preview>
            <CodeBlock
                code={ISLAND_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("directionProviderPage.a11yDir"),
                    t("directionProviderPage.a11yLang"),
                    t("directionProviderPage.a11yConflict"),
                    t("directionProviderPage.a11yConsumers"),
                    t("directionProviderPage.a11yNative"),
                    t("directionProviderPage.a11yRole")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("directionProviderPage.propDirection"),
                        type: "\"ltr\" | \"rtl\"",
                        name: "direction"
                    },
                    {
                        description: t("directionProviderPage.propLang"),
                        type: "string",
                        name: "lang"
                    },
                    {
                        description: t("directionProviderPage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("directionProviderPage.propsNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    NCoreUIKitLocalize.useContext().direction
                </InlineCode>
                {" "}
                {t("directionProviderPage.hookText")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default DirectionProviderPage;
